/**
 * Presentation shaping for the daypass ("Tageskarten") block of the club detail.
 *
 * The block is aggregated from the daypass purchases: `sold` and `buyers` count the purchases
 * completed in the range, `visits` counts the booked days by the day a daypass is valid for.
 */

export interface DaypassTemplateStat {
  /** Null for a sale whose version no template lists any more — grouped by its name. */
  templateId: string | null;
  name: string;
  sold: number;
}

export interface DaypassStats {
  sold: number;
  buyers: number;
  templates: DaypassTemplateStat[];
  visits: {
    total: number;
    labels: string[];
    series: Array<{ visits: number }>;
  };
  compare?: { sold: number; buyers: number };
}

export interface DaypassTemplateRow extends DaypassTemplateStat {
  /** Stable React key — templateId alone is null for sales grouped by name. */
  key: string;
  /** Percentage of the club's daypass sales in the selected range, 0 when nothing was sold. */
  share: number;
}

/**
 * Rows of the per-template breakdown, strongest seller first. Ties are broken by name so the
 * list does not reshuffle between renders of the same data.
 */
export function daypassTemplateRows(
  daypass: DaypassStats | null | undefined
): DaypassTemplateRow[] {
  const total = daypass?.sold ?? 0;

  return [...(daypass?.templates ?? [])]
    .map((template) => {
      const sold = template.sold ?? 0;
      return {
        ...template,
        sold,
        key: template.templateId ?? `name:${template.name}`,
        share: total > 0 ? (sold / total) * 100 : 0,
      };
    })
    .sort((a, b) => b.sold - a.sold || a.name.localeCompare(b.name, "de"));
}

/** Booked visit days per axis position, index-aligned to the labels the response ships. */
export function daypassChartPoints(daypass: DaypassStats | null | undefined): {
  labels: string[];
  visits: number[];
} {
  const labels = daypass?.visits?.labels ?? [];
  return {
    labels,
    visits: labels.map((_, i) => daypass?.visits?.series?.[i]?.visits ?? 0),
  };
}

/**
 * Change of the sold daypasses against the comparison period in percent, rounded to one decimal.
 * Null whenever it would not mean anything: no comparison was requested, or the comparison period
 * sold nothing so every change would be an infinite increase.
 */
export function daypassChange(
  daypass: DaypassStats | null | undefined
): number | null {
  const reference = daypass?.compare?.sold;
  if (!reference) return null;

  return Number((((daypass.sold - reference) / reference) * 100).toFixed(1));
}

/** Daypasses per buyer, rounded to two decimals. Null without a buyer — 0/0 is not a ratio. */
export function daypassPerBuyer(
  daypass: DaypassStats | null | undefined
): number | null {
  if (!daypass?.buyers) return null;
  return Number((daypass.sold / daypass.buyers).toFixed(2));
}
